/**
 * io.ai's set_page_setup: margins, orientation and paper size for the whole
 * document, or orientation for one part of it (given its own section). Word
 * measures in points; the agent passes the user's own units and the
 * conversion happens here. Page setup needs WordApiDesktop 1.3 (Word for
 * Windows, Mac and iPad); elsewhere the result is "unsupported" with the
 * values in words, which io.ai passes on.
 */
import { isOfficeDocumentAvailable } from "./snapshot";
import { locateParagraph } from "./wordTools";

export type LengthUnit = "cm" | "mm" | "in" | "pt";
export type PageSetupRequest = {
  margins?: { top?: number; bottom?: number; left?: number; right?: number; unit?: LengthUnit };
  orientation?: "portrait" | "landscape";
  paper_size?: "A4" | "A3" | "A5" | "Letter" | "Legal";
  start_index?: number;
  end_index?: number;
  expected_text?: string;
};

export type PageSetupResult =
  | { status: "applied"; applied: string[] }
  | { status: "unsupported"; reason: string; values: string }
  | { status: "stale"; current_text?: string }
  | { status: "error"; message: string };

const POINTS: Record<LengthUnit, number> = { pt: 1, in: 72, cm: 72 / 2.54, mm: 72 / 25.4 };
export const toPoints = (value: number, unit: LengthUnit = "cm"): number => Math.round(value * POINTS[unit] * 10) / 10;

export const pageSetupSupported = (): boolean => {
  try {
    return isOfficeDocumentAvailable() && Office.context.requirements.isSetSupported("WordApiDesktop", "1.3");
  } catch {
    return false;
  }
};

const describe = (r: PageSetupRequest): string => {
  const bits: string[] = [];
  const m = r.margins;
  if (m) {
    const u = m.unit ?? "cm";
    const side = (name: string, v?: number) => (typeof v === "number" ? `${name} ${v} ${u}` : null);
    bits.push(
      `Margins: ${[side("Top", m.top), side("Bottom", m.bottom), side("Left", m.left), side("Right", m.right)].filter(Boolean).join(", ")}`
    );
  }
  if (r.orientation) bits.push(`Orientation: ${r.orientation}`);
  if (r.paper_size) bits.push(`Size: ${r.paper_size}`);
  return bits.join(". ");
};

export const applyPageSetup = async (request: PageSetupRequest): Promise<PageSetupResult> => {
  if (!pageSetupSupported()) {
    return {
      status: "unsupported",
      reason: "This Word doesn't let add-ins change page setup (Word on the web or an older Word).",
      values: describe(request),
    };
  }
  try {
    return await Word.run(async (context) => {
      const applied: string[] = [];
      if (typeof request.start_index === "number" && typeof request.end_index === "number") {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text");
        await context.sync();
        const texts = paragraphs.items.map((p) => p.text ?? "");
        const start = locateParagraph(texts, request.start_index, request.expected_text ?? "");
        if (start === null) return { status: "stale" as const, current_text: texts[request.start_index] };
        const end = Math.min(texts.length - 1, start + Math.max(0, request.end_index - request.start_index));
        // A section of its own: breaks before the first paragraph and after the
        // last, but not at the document's edges, where a break would only add
        // an empty page.
        if (start > 0) paragraphs.items[start].getRange("Whole").insertBreak("SectionNext", "Before");
        if (end < texts.length - 1) paragraphs.items[end].getRange("Whole").insertBreak("SectionNext", "After");
        await context.sync();
        const section = paragraphs.items[start].getRange("Whole").sections.getFirst();
        if (request.orientation)
          section.pageSetup.orientation = request.orientation === "landscape" ? "Landscape" : "Portrait";
        await context.sync();
        applied.push(`${request.orientation ?? "orientation"} for paragraphs ${start}–${end}, in their own section`);
        return { status: "applied" as const, applied };
      }
      const page = context.document.pageSetup;
      const m = request.margins;
      if (m) {
        const unit = m.unit ?? "cm";
        if (typeof m.top === "number") page.topMargin = toPoints(m.top, unit);
        if (typeof m.bottom === "number") page.bottomMargin = toPoints(m.bottom, unit);
        if (typeof m.left === "number") page.leftMargin = toPoints(m.left, unit);
        if (typeof m.right === "number") page.rightMargin = toPoints(m.right, unit);
        applied.push("margins");
      }
      if (request.orientation) {
        page.orientation = request.orientation === "landscape" ? "Landscape" : "Portrait";
        applied.push(`${request.orientation} orientation`);
      }
      if (request.paper_size) {
        page.paperSize = request.paper_size;
        applied.push(`${request.paper_size} paper`);
      }
      await context.sync();
      return { status: "applied" as const, applied };
    });
  } catch (error) {
    return { status: "error", message: error instanceof Error ? error.message : String(error) };
  }
};
